import { useState } from 'react';
import { Button } from '@design-system';
import { advisorAdminApi } from '../api/api';
import { getErrorMessage } from '../utils/get-error-message';

/**
 * Wizard admin tool: export a client (+ partner + all related rows) as a JSON
 * bundle, and import a bundle into the current environment's DB. Names are
 * anonymized by default. Import is disabled in production (enforced server-side).
 */
export function CloneClientForm() {
  const [clientId, setClientId] = useState('');
  const [anonymize, setAnonymize] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleExport() {
    const id = clientId.trim();
    if (!id) return;
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const bundle = await advisorAdminApi.exportClient({
        clientId: id,
        anonymizeNames: anonymize,
      });
      const blob = new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `client-export-${id}.json`;
      a.click();
      URL.revokeObjectURL(url);
      const rows = Object.values(bundle.tables).reduce((n, r) => n + r.length, 0);
      setMessage(`Exported ${Object.keys(bundle.tables).length} tables (${rows} rows) for ${bundle.clientIds.join(', ')}.`);
    } catch (e) {
      setError(getErrorMessage(e as Error, 'Export failed'));
    } finally {
      setBusy(false);
    }
  }

  async function handleImport(file: File) {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const bundle = JSON.parse(await file.text());
      const res = await advisorAdminApi.importClient(bundle);
      const inserted = Object.values(res.imported).reduce((n, t) => n + t.inserted, 0);
      const newIds = Object.values(res.clientIdMap).join(', ');
      setMessage(`Imported ${inserted} rows. New client id: ${newIds || '(none)'}`);
    } catch (e) {
      setError(getErrorMessage(e as Error, 'Import failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium">
        Client ID
        <input
          className="mt-1 block w-full rounded border border-gray-300 px-3 py-2"
          placeholder="e.g. u49-f7k9"
          value={clientId}
          onChange={e => setClientId(e.target.value)}
        />
      </label>

      <div className="flex flex-wrap items-center gap-4">
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={anonymize} onChange={e => setAnonymize(e.target.checked)} />
          Anonymize names
        </label>
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={handleExport} disabled={!clientId.trim() || busy}>
          Export to JSON
        </Button>
        <Button variant="outline" disabled={busy} asChild>
          <label className="cursor-pointer">
            Import from JSON
            <input
              type="file"
              accept="application/json"
              className="hidden"
              onChange={e => {
                const f = e.target.files?.[0];
                if (f) void handleImport(f);
                e.target.value = '';
              }}
            />
          </label>
        </Button>
      </div>

      {message && <p className="text-sm text-green-700">{message}</p>}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}
